import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, ArrowRight, Phone, Upload } from "lucide-react";
import { api, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AVAIL = [
  { id: "day", label: "Jour" },
  { id: "evening", label: "Soir" },
  { id: "weekend", label: "Fin de semaine" },
  { id: "variable", label: "Variable" },
];
const VEHICLE = [
  { id: "car", label: "Voiture" },
  { id: "truck", label: "Camion / pick-up" },
  { id: "other", label: "Autre" },
];

export default function HelperApply() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [existing, setExisting] = useState(null);
  const [phone, setPhone] = useState(user.phone || "");
  const [city, setCity] = useState(user.area || "");
  const [willingToTravel, setWillingToTravel] = useState(true);
  const [maxKm, setMaxKm] = useState(15);
  const [availability, setAvailability] = useState([]);
  const [hasVehicle, setHasVehicle] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [taskCategories, setTaskCategories] = useState([]);
  const [hasExperience, setHasExperience] = useState(false);
  const [experienceDetails, setExperienceDetails] = useState("");
  const [whyGood, setWhyGood] = useState("");
  const [rules, setRules] = useState(false);
  const [payment, setPayment] = useState(false);
  const [referredBy, setReferredBy] = useState("");
  const [consent, setConsent] = useState(false);
  const [idPhoto, setIdPhoto] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/helper/application/me").then((r) => {
      setExisting(r.data);
      if (r.data?.id_photo_path) setIdPhoto(r.data.id_photo_path);
      if (r.data?.profile_photo_path) setProfilePhoto(r.data.profile_photo_path);
    });
  }, []);

  const uploadPhoto = async (kind, file, setBusy, setPath) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 Mo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/helper/application/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPath(data.path);
      toast.success("Photo téléversée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally { setBusy(false); }
  };

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const submit = async () => {
    if (!phone || !city || !whyGood) { toast.error("Champs obligatoires manquants"); return; }
    if (!rules || !payment || !consent) { toast.error("Tu dois accepter les 3 conditions finales"); return; }
    if (!availability.length) { toast.error("Sélectionne au moins une disponibilité"); return; }
    if (!taskCategories.length) { toast.error("Choisis au moins une catégorie"); return; }
    if (!idPhoto || !profilePhoto) { toast.error("Photo d'identité et photo de profil requises"); return; }
    setLoading(true);
    try {
      await api.post("/helper/application", {
        phone, city,
        willing_to_travel: willingToTravel,
        max_km: willingToTravel ? Number(maxKm) : null,
        availability, has_vehicle: hasVehicle,
        vehicle_type: hasVehicle ? vehicleType : null,
        task_categories: taskCategories,
        has_experience: hasExperience,
        experience_details: hasExperience ? experienceDetails : null,
        why_good_helper: whyGood,
        accepts_platform_rules: rules,
        accepts_platform_payment: payment,
        referred_by_name: referredBy || null,
        info_accurate_consent: consent,
      });
      await refresh();
      toast.success("Candidature envoyée !");
      nav("/dashboard/helper");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  // Already submitted state
  if (existing && existing.status && existing.status !== "not_submitted") {
    const st = existing.status;
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-3xl font-heading font-black text-navy">Ma candidature</h1>
        <div className={`card-surface p-6 ${st === "approved" ? "ring-2 ring-lime" : st === "rejected" ? "ring-2 ring-red-300" : ""}`} data-testid="application-status">
          <div className="flex items-center gap-3">
            {st === "approved" && <CheckCircle2 className="text-lime" size={32} />}
            {st === "pending" && <Clock className="text-amber-500" size={32} />}
            {st === "rejected" && <XCircle className="text-red-500" size={32} />}
            <div>
              <div className="font-heading font-black text-xl text-navy">
                {st === "approved" && "Candidature approuvée !"}
                {st === "pending" && "Candidature en attente"}
                {st === "rejected" && "Candidature refusée"}
              </div>
              <div className="text-sm text-[#475569]">
                {st === "approved" && "Tu peux maintenant accepter des tâches."}
                {st === "pending" && "L'équipe Go Helper examine ton dossier. Réponse sous 24-48h."}
                {st === "rejected" && (existing.admin_reason || "Tu peux modifier et resoumettre ta candidature.")}
              </div>
            </div>
          </div>
          {st !== "approved" && (
            <button onClick={() => setExisting(null)} className="btn-outline mt-4" data-testid="edit-application">
              Modifier ma candidature
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <span className="chip-lime">Candidature helper</span>
        <h1 className="text-3xl md:text-4xl font-heading font-black text-navy mt-3">Rejoins Go Helper</h1>
        <p className="text-[#475569] mt-1">Remplis ce formulaire (2-3 min). On te contacte rapidement.</p>
      </div>

      <Section n="1" t="Informations de base">
        <Field label="Téléphone *"><input className="input-base" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="app-phone" /></Field>
        <Field label="Email" hint="Déjà enregistré"><input className="input-base bg-surface" value={user.email} disabled /></Field>
      </Section>

      <Section n="2" t="Localisation">
        <Field label="Ville / secteur *"><input className="input-base" value={city} onChange={(e) => setCity(e.target.value)} data-testid="app-city" /></Field>
        <YesNo val={willingToTravel} set={setWillingToTravel} label="Prêt à te déplacer ?" testid="app-travel" />
        {willingToTravel && (
          <Field label="Jusqu'à combien de km ?">
            <div className="flex gap-2 flex-wrap">
              {[5, 10, 15, 25].map((k) => (
                <button key={k} type="button" onClick={() => setMaxKm(k)}
                  className={`chip ${maxKm === k ? "bg-navy text-white" : ""}`} data-testid={`app-km-${k}`}>{k} km</button>
              ))}
            </div>
          </Field>
        )}
      </Section>

      <Section n="3" t="Disponibilités">
        <div className="grid grid-cols-2 gap-2">
          {AVAIL.map((a) => (
            <button key={a.id} type="button" onClick={() => toggle(availability, setAvailability, a.id)}
              className={`p-3 rounded-xl border-2 font-bold text-sm ${availability.includes(a.id) ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy"}`}
              data-testid={`app-avail-${a.id}`}>{a.label}</button>
          ))}
        </div>
      </Section>

      <Section n="4" t="Transport">
        <YesNo val={hasVehicle} set={setHasVehicle} label="As-tu un véhicule ?" testid="app-vehicle" />
        {hasVehicle && (
          <div className="flex gap-2 flex-wrap mt-2">
            {VEHICLE.map((v) => (
              <button key={v.id} type="button" onClick={() => setVehicleType(v.id)}
                className={`chip ${vehicleType === v.id ? "bg-navy text-white" : ""}`} data-testid={`app-vtype-${v.id}`}>{v.label}</button>
            ))}
          </div>
        )}
      </Section>

      <Section n="5" t="Types de tâches">
        <p className="text-xs text-[#475569]">Sélectionne celles que tu peux faire.</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {CATEGORIES.map((c) => (
            <button key={c.id} type="button" onClick={() => toggle(taskCategories, setTaskCategories, c.id)}
              className={`chip ${taskCategories.includes(c.id) ? "bg-navy text-white" : ""}`} data-testid={`app-cat-${c.id}`}>{c.label}</button>
          ))}
        </div>
      </Section>

      <Section n="6" t="Expérience">
        <YesNo val={hasExperience} set={setHasExperience} label="As-tu de l'expérience dans certaines tâches ?" testid="app-exp" />
        {hasExperience && (
          <Field label="Précise">
            <textarea rows={3} className="input-base" value={experienceDetails} onChange={(e) => setExperienceDetails(e.target.value)} data-testid="app-exp-details" />
          </Field>
        )}
      </Section>

      <Section n="7" t="Fiabilité">
        <Field label="Pourquoi serais-tu un bon helper ? *">
          <textarea rows={3} className="input-base" value={whyGood} onChange={(e) => setWhyGood(e.target.value)} placeholder="Ponctuel, sérieux, motivé..." data-testid="app-why" />
        </Field>
      </Section>

      <Section n="8" t="Engagement (obligatoire)">
        <Check val={rules} set={setRules} testid="app-rules"
          label="J'accepte de passer par Go Helper pour toutes les demandes, de ne pas contacter les clients directement, et de respecter les règles du service." />
      </Section>

      <Section n="9" t="Paiement">
        <Check val={payment} set={setPayment} testid="app-payment"
          label="J'accepte d'être payé via Go Helper." />
      </Section>

      <Section n="10" t="Parrainage">
        <Field label="As-tu été référé par quelqu'un ? (optionnel)">
          <input className="input-base" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} placeholder="Nom de la personne" data-testid="app-referrer" />
        </Field>
      </Section>

      <Section n="11" t="Consentement">
        <Check val={consent} set={setConsent} testid="app-consent"
          label="Je confirme que les informations sont exactes et je souhaite rejoindre le réseau Go Helper." />
      </Section>

      <Section n="12" t="Photos (obligatoires)">
        <div className="grid sm:grid-cols-2 gap-4">
          <PhotoField
            label="Photo d'identité (permis / carte)"
            path={idPhoto}
            busy={uploadingId}
            onChange={(f) => uploadPhoto("id_photo", f, setUploadingId, setIdPhoto)}
            testid="upload-id"
          />
          <PhotoField
            label="Photo de profil (visage clair)"
            path={profilePhoto}
            busy={uploadingProfile}
            onChange={(f) => uploadPhoto("profile_photo", f, setUploadingProfile, setProfilePhoto)}
            testid="upload-profile"
          />
        </div>
      </Section>

      <div className="card-surface p-6 flex items-start gap-3 bg-lime/10 border-2 border-lime">
        <Phone className="text-navy shrink-0" size={22} />
        <div className="text-sm text-navy">
          <strong>Entrevue téléphonique</strong> — après avoir envoyé ta candidature, un membre de Go Helper te contactera pour une courte entrevue avant l'approbation finale.
        </div>
      </div>

      <button onClick={submit} disabled={loading} className="btn-primary w-full py-4 disabled:opacity-50" data-testid="submit-application">
        {loading ? "Envoi..." : "Envoyer ma candidature"} <ArrowRight size={18} />
      </button>
    </div>
  );
}

function Section({ n, t, children }) {
  return (
    <div className="card-surface p-6 space-y-3">
      <div className="flex items-center gap-2">
        <span className="font-mono font-bold text-lime">{n}</span>
        <h2 className="font-heading font-black text-lg text-navy">{t}</h2>
      </div>
      {children}
    </div>
  );
}
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-bold text-navy">{label}</label>
      {hint && <span className="text-xs text-[#94A3B8] ml-2">{hint}</span>}
      <div className="mt-1">{children}</div>
    </div>
  );
}
function YesNo({ val, set, label, testid }) {
  return (
    <div>
      <div className="text-sm font-bold text-navy mb-2">{label}</div>
      <div className="flex gap-2">
        <button type="button" onClick={() => set(true)} className={`chip ${val ? "bg-navy text-white" : ""}`} data-testid={`${testid}-yes`}>Oui</button>
        <button type="button" onClick={() => set(false)} className={`chip ${!val ? "bg-navy text-white" : ""}`} data-testid={`${testid}-no`}>Non</button>
      </div>
    </div>
  );
}
function Check({ val, set, label, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input type="checkbox" checked={val} onChange={(e) => set(e.target.checked)} className="h-5 w-5 accent-[#A3E635] mt-0.5" data-testid={testid} />
      <span className="text-sm text-navy font-medium leading-relaxed">{label}</span>
    </label>
  );
}

function PhotoField({ label, path, busy, onChange, testid }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("gh_token") : null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const previewUrl = path ? `${backend}/api/files/${path}?auth=${token}` : null;
  return (
    <div>
      <label className="text-sm font-bold text-navy">{label}</label>
      <div className="mt-2 border-2 border-dashed border-[#E2E8F0] rounded-xl p-4 flex flex-col items-center justify-center gap-2 min-h-[160px]">
        {previewUrl ? (
          <img src={previewUrl} alt="preview" className="max-h-32 rounded-lg object-contain" />
        ) : (
          <Upload size={24} className="text-[#94A3B8]" />
        )}
        <label className="btn-outline py-2 px-3 text-xs cursor-pointer">
          {busy ? "Envoi…" : path ? "Remplacer" : "Choisir un fichier"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => onChange(e.target.files?.[0])} data-testid={testid} />
        </label>
        <p className="text-[10px] text-[#94A3B8]">JPG/PNG/WebP · Max 5 Mo</p>
      </div>
    </div>
  );
}
